import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { getDomains, updateDomainFavorite, getPageHits, getAdSenseEarnings, getAdImpressions, getAmazonOffers, getReadTime } from './api';
import { offersForDomain } from './offerMetrics';
import { getScheduledPages, getAdSenseDaily, getAmazonEarnings } from './apiAnalytics';
import { getAlertSummary, ALERTS_CHANGED } from './apiEmails';
import { useDomain } from './DomainContext';
import Spinner from './ui/Spinner';
import PageHeader from './ui/PageHeader';
import EmptyState from './ui/EmptyState';
import ScheduledCard from './landing/ScheduledCard';
import AttentionCard from './landing/AttentionCard';
import KpiStrip from './landing/KpiStrip';
import SitesTable from './landing/SitesTable';
import { VIEWS as COLUMN_VIEWS, DEFAULT_VIEW, viewOf } from './landing/siteColumns';
import AmazonPanel, { AmazonImportModal } from './landing/AmazonPanel';
import DoNextCard from './donext/DoNextCard';
import WeekCard from './donext/WeekCard';
import { dataFreshness, asOfLabel } from './donext/doNext';
import { buildPeriods, adsenseForPeriod, amazonForPeriod, periodEarnings } from './earnings';
import {
  mstDayWindow,
  normalizeHost,
  sumByBaseHost,
  mergeDailyByBaseHost,
  sumOverDays,
  needsAttention,
  kpiTotals,
  relativeTime,
  latestTimestamp,
} from './domainMetrics';

const SCHEDULE_DAYS = 7;
const COLUMNS_KEY = 'sites.columns';
const PERIOD_KEY = 'sites.earningsPeriod';

/* localStorage can throw (private mode, blocked storage); the column preset
   just falls back to the default. */
const readColumnsPref = () => {
  try { return viewOf(localStorage.getItem(COLUMNS_KEY)).key; } catch { return DEFAULT_VIEW; }
};
const writeColumnsPref = (key) => {
  try { localStorage.setItem(COLUMNS_KEY, key); } catch { /* ignore */ }
};
const readPeriodPref = () => {
  try { return localStorage.getItem(PERIOD_KEY) || 'last30'; } catch { return 'last30'; }
};
const writePeriodPref = (id) => {
  try { localStorage.setItem(PERIOD_KEY, id); } catch { /* ignore */ }
};

/* "Data as of 3:00 AM today", with a warning only when a source is stale or
   could not be read. The scheduled jobs refresh every source on their own;
   the manual refresh buttons live in Settings → Data sources. */
const FreshnessLine = ({ freshness, now }) => {
  if (!freshness) return <span>Loading data…</span>;
  const { asOf, problems } = freshness;
  return (
    <span>
      {asOf ? `Data as of ${asOfLabel(asOf, now)}` : 'Data freshness unknown'}
      {problems.length > 0 && (
        <span style={{ color: '#92400e' }}>
          {' · ⚠ '}
          {problems.map((p) => (p.state === 'stale' ? `${p.label.toLowerCase()} out of date`
            : `${p.label.toLowerCase()} unavailable`)).join(', ')}
          {' · '}
          <Link to="/settings#data-sources">Data sources</Link>
        </span>
      )}
    </span>
  );
};

/* view="home" is the console home (/): the week and the ranked Do next list.
   view="sites" is /domains: every site with traffic, earnings and Amazon. */
const Domains = ({ view = 'sites' }) => {
  const isHome = view === 'home';
  const [domains, setDomains] = useState([]);
  const [pageHits, setPageHits] = useState({});
  const [pageHitsDaily, setPageHitsDaily] = useState({});
  /* probably-human views (page_hits_30d.human / human_daily); null until the
     refresh job has labelled views at least once */
  const [pageHuman, setPageHuman] = useState(null);
  const [pageHumanDaily, setPageHumanDaily] = useState({});
  const [humanRule, setHumanRule] = useState(null);
  const [adsenseEarnings, setAdsenseEarnings] = useState({});
  const [adImpressions, setAdImpressions] = useState({});
  const [amazonOffers, setAmazonOffers] = useState(null);
  const [readTime, setReadTime] = useState(null);
  /* inbox alert summary: null while loading, { error } when it failed */
  const [messages, setMessages] = useState(null);
  const [adsenseDaily, setAdsenseDaily] = useState(null);
  const [amazon, setAmazon] = useState({ periods: [], tags: {} });
  const [periodId, setPeriodId] = useState(readPeriodPref);
  const [showImport, setShowImport] = useState(false);
  const [updatedAt, setUpdatedAt] = useState({ hits: null, earnings: null, impressions: null, amazon: null });
  const [scheduled, setScheduled] = useState({ pages: [], loading: true, failed: false });
  const [searchQuery, setSearchQuery] = useState('');
  const [attentionOnly, setAttentionOnly] = useState(false);
  const [columnsView, setColumnsView] = useState(readColumnsPref);
  const [loadingDomains, setLoadingDomains] = useState(true);
  const [error, setError] = useState(null);
  /* which analytics sources have answered (so freshness and thin-page
     candidates wait for real numbers instead of reading empty maps) */
  const [loaded, setLoaded] = useState({});
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const navigate = useNavigate();
  const { selectDomain } = useDomain();

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  /* ── fetchers, one per source so a refresh can re-pull just its own map ── */
  const fetchDomains = useCallback(async () => {
    try {
      const data = await getDomains();
      setDomains(Array.isArray(data) ? data : []);
    } catch (err) {
      if (err.response && err.response.status === 401) {
        navigate('/login');
      } else {
        setError(err.response?.data?.detail || 'Failed to fetch domains');
      }
    } finally {
      /* an empty list is a real answer, not "still loading" */
      setLoadingDomains(false);
    }
  }, [navigate]);

  const fetchers = useMemo(() => ({
    hits: async () => {
      const data = await getPageHits();
      setPageHits(data.hits || {});
      setPageHitsDaily(data.daily || {});
      const labelled = data.human && Object.keys(data.human).length > 0;
      setPageHuman(labelled ? data.human : null);
      setPageHumanDaily(labelled ? (data.human_daily || {}) : {});
      setHumanRule((data.human_rule && data.human_rule.text) || null);
      setUpdatedAt((u) => ({ ...u, hits: data.updated_at || null }));
      setLoaded((l) => ({ ...l, hits: true }));
    },
    earnings: async () => {
      const [data, daily] = await Promise.all([getAdSenseEarnings(), getAdSenseDaily()]);
      setAdsenseEarnings(data.earnings || {});
      setAdsenseDaily(daily);
      setUpdatedAt((u) => ({ ...u, earnings: data.updated_at || null }));
      setLoaded((l) => ({ ...l, earnings: true }));
    },
    amazon: async () => {
      const data = await getAmazonEarnings();
      setAmazon(data);
      setUpdatedAt((u) => ({ ...u, amazon: data.updated_at || null }));
      setLoaded((l) => ({ ...l, amazon: true }));
    },
    impressions: async () => {
      const data = await getAdImpressions();
      setAdImpressions(data.domains || {});
      setUpdatedAt((u) => ({ ...u, impressions: data.updated_at || null }));
      setLoaded((l) => ({ ...l, impressions: true }));
    },
    stats: fetchDomains,
  }), [fetchDomains]);

  useEffect(() => {
    fetchDomains();
    fetchers.hits();
    fetchers.earnings();
    fetchers.impressions();
    fetchers.amazon();
    getAmazonOffers().then(setAmazonOffers).catch(() => setAmazonOffers(null));
    getReadTime().then(setReadTime);
    getScheduledPages(SCHEDULE_DAYS).then((data) =>
      setScheduled({ pages: data.pages, loading: false, failed: !!data.error }));
  }, [fetchDomains, fetchers]);

  /* Read once here for both cards; re-read whenever the inbox marks a message
     handled (it fires ALERTS_CHANGED) so an answered item leaves the list. */
  useEffect(() => {
    let cancelled = false;
    const load = () => getAlertSummary()
      .then((d) => { if (!cancelled) setMessages(d || {}); })
      .catch(() => { if (!cancelled) setMessages({ error: true }); });
    load();
    window.addEventListener(ALERTS_CHANGED, load);
    return () => { cancelled = true; window.removeEventListener(ALERTS_CHANGED, load); };
  }, []);

  const handleFavoriteToggle = async (domainId, currentFavoriteStatus) => {
    try {
      const newFavoriteStatus = !currentFavoriteStatus;
      await updateDomainFavorite(domainId, newFavoriteStatus);
      setDomains((prev) => prev.map((d) => (d.id === domainId ? { ...d, is_favorite: newFavoriteStatus } : d)));
    } catch (err) {
      if (err.response && err.response.status === 401) {
        navigate('/login');
      } else {
        setError(err.response?.data?.detail || 'Failed to update favorite status');
      }
    }
  };

  const selectColumns = (key) => { setColumnsView(key); writeColumnsPref(key); };

  /* ── per-domain metric accessors (apex-keyed, 30 complete MST days) ── */
  const trendDays = useMemo(() => mstDayWindow(30), []);
  const hitsByHost = useMemo(() => sumByBaseHost(pageHits), [pageHits]);
  const hitsDailyByHost = useMemo(() => mergeDailyByBaseHost(pageHitsDaily), [pageHitsDaily]);
  const humanByHost = useMemo(() => (pageHuman ? sumByBaseHost(pageHuman) : null), [pageHuman]);
  const humanDailyByHost = useMemo(() => mergeDailyByBaseHost(pageHumanDaily), [pageHumanDaily]);
  const earningsByHost = useMemo(() => sumByBaseHost(adsenseEarnings), [adsenseEarnings]);
  const earningsDailyByHost = useMemo(
    () => mergeDailyByBaseHost(adsenseDaily && adsenseDaily.daily), [adsenseDaily]);

  const metrics = useMemo(() => {
    const baseHostOf = (d) => normalizeHost(d.hostname || d.name || '');
    const hits = (d) => hitsByHost[baseHostOf(d)] || 0;
    const hitsDaily = (d) => hitsDailyByHost[baseHostOf(d)] || {};
    /* Probably-human views; null per site while unlabelled. trafficDaily is
       the series the week card, movers and drop alerts judge: human views
       when labelled (crawlers come in bursts), every view otherwise. */
    const humanHits = (d) => (humanByHost ? humanByHost[baseHostOf(d)] || 0 : null);
    const humanDaily = (d) => humanDailyByHost[baseHostOf(d)] || {};
    const trafficDaily = humanByHost ? humanDaily : undefined;
    const earnings = (d) => earningsByHost[baseHostOf(d)] || 0;
    const earningsDaily = (d) => earningsDailyByHost[baseHostOf(d)] || {};
    const imprDaily = (d) => {
      const host = d.hostname || d.name || '';
      const entry = adImpressions[host] || adImpressions[host.replace(/\./g, '_')]
        || adImpressions[d.name] || adImpressions['www.' + host];
      return entry ? (entry.daily || {}) : {};
    };
    /* The stored impressions total includes today's partial day; re-summing
       over trendDays puts it on the same window as hits and AdSense's
       LAST_30_DAYS so the columns divide into each other cleanly. */
    const impressions = (d) => sumOverDays(imprDaily(d), trendDays);
    const adsPerHit = (d) => (hits(d) > 0 ? impressions(d) / hits(d) : null);
    const ecpm = (d) => (impressions(d) > 0 ? (earnings(d) / impressions(d)) * 1000 : null);
    /* RPM is per 1,000 page views, so it covers sites with no ad tracking too */
    const rpm = (d) => (hits(d) > 0 ? (earnings(d) / hits(d)) * 1000 : null);
    /* Amazon offers are keyed by domain id (the Snowplow app_id), already on
       the same 30 complete MST days. */
    const amzImpr = (d) => (offersForDomain(amazonOffers, d.id) || {}).impressions || 0;
    const amzClicks = (d) => (offersForDomain(amazonOffers, d.id) || {}).clicks || 0;
    /* Read time is keyed by domain id too. A site missing from it had no
       tracked views, so its rate is unknown ("—") rather than 0%. */
    const read = (d) => (readTime && readTime.domains && readTime.domains[d.id]) || null;
    const readRate = (d) => (read(d) ? read(d).read_rate : null);
    const readTimeS = (d) => (read(d) ? read(d).read_median_s : null);
    const readers = (d) => (read(d) ? read(d).readers : 0);
    const readViews = (d) => (read(d) ? read(d).views_tracked : 0);
    const readTotals = readTime ? readTime.totals : null;
    return {
      hits, hitsDaily, humanHits, humanDaily, trafficDaily, humanRule, earnings, earningsDaily, impressions, imprDaily, adsPerHit, ecpm, rpm, amzImpr, amzClicks,
      readRate, readTime: readTimeS, readers, readViews, readTotals,
    };
  }, [hitsByHost, hitsDailyByHost, humanByHost, humanDailyByHost, humanRule, earningsByHost, earningsDailyByHost, adImpressions, amazonOffers, readTime, trendDays]);

  /* ── earnings for the selected period (AdSense + Amazon) ── */
  const periods = useMemo(() => buildPeriods(amazon.periods), [amazon.periods]);
  const period = periods.find((p) => p.id === periodId) || periods[0];
  const adsensePeriod = useMemo(
    () => adsenseForPeriod(period, { earnings30: adsenseEarnings, daily: adsenseDaily }),
    [period, adsenseEarnings, adsenseDaily]);
  const amazonPeriod = useMemo(() => amazonForPeriod(period, amazon), [period, amazon]);
  const selectPeriod = (id) => { setPeriodId(id); writePeriodPref(id); };

  const filteredDomains = domains
    .filter((domain) =>
      Object.values(domain).some((value) => String(value).toLowerCase().includes(searchQuery.toLowerCase())))
    .filter((domain) => !attentionOnly || needsAttention(domain));

  const attentionCount = domains.filter(needsAttention).length;
  const kpis = kpiTotals(filteredDomains, metrics);
  const periodTotals = periodEarnings(filteredDomains, adsensePeriod, amazonPeriod);
  const earningsInfo = { period, adsensePeriod, amazonPeriod, ...periodTotals };
  const domainsById = Object.fromEntries(domains.map((d) => [d.id, d]));
  const totalPages = domains.reduce((acc, d) => acc + (Number(d.page_count) || 0), 0);
  const statsUpdatedAt = latestTimestamp(domains, 'page_stats_updated_at');
  const statsAge = relativeTime(statsUpdatedAt);
  const now = new Date();
  const metricsReady = !!(loaded.hits && loaded.earnings);
  const freshness = metricsReady && loaded.impressions && !loadingDomains
    ? dataFreshness({ ...updatedAt, stats: statsUpdatedAt }, {}, now) : null;

  if (loadingDomains && !error) {
    return (
      <div className="page">
        <Spinner size={40} label="Loading sites…" />
      </div>
    );
  }

  const subtitle = [
    `${domains.length} site${domains.length === 1 ? '' : 's'}`,
    `${totalPages.toLocaleString()} page${totalPages === 1 ? '' : 's'}`,
    statsAge ? `last stats refresh ${statsAge}` : 'stats never refreshed',
  ].join(' · ');

  const sitesActions = (
    <div className="chip-group" role="group" aria-label="Site data">
      <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowImport(true)}
              title="Upload a report downloaded from Amazon Associates Central">
        Import Amazon report
      </button>
      <Link to="/settings#data-sources" className="btn btn-ghost btn-sm"
            title="When each source was last refreshed, and manual refresh">
        Data sources
      </Link>
    </div>
  );

  const importModal = showImport && (
    <AmazonImportModal
      domainsById={domainsById}
      onClose={() => setShowImport(false)}
      onSaved={(result) => {
        fetchers.amazon();
        if (result && result.period_id) selectPeriod(`amazon:${result.period_id}`);
      }}
    />
  );

  if (isHome) return (
    <div className="page">
      <PageHeader title="Do next" subtitle={<FreshnessLine freshness={freshness} now={now} />} />
      {error && <div className="alert alert-error" style={{ marginBottom: '16px' }}>{error}</div>}

      <WeekCard
        domains={domains}
        metrics={metrics}
        ready={metricsReady}
        adsenseDaily={loaded.earnings ? adsenseDaily : null}
        messages={messages}
        onSelectDomain={selectDomain}
      />

      <DoNextCard
        domains={domains}
        metrics={metrics}
        ready={metricsReady}
        freshness={freshness}
        amazon={loaded.amazon ? amazon : null}
        messages={messages}
        onAmazonImport={() => setShowImport(true)}
        onSelectDomain={selectDomain}
      />

      <p className="small muted">
        <Link to="/domains">All sites →</Link>
      </p>

      {importModal}
    </div>
  );

  return (
    <div className="page" id="sites">
      <PageHeader
        title="Sites"
        subtitle={<>{subtitle} · <FreshnessLine freshness={freshness} now={now} /></>}
        actions={sitesActions}
      />
      {error && <div className="alert alert-error" style={{ marginBottom: '16px' }}>{error}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '16px', alignItems: 'start', marginBottom: '20px' }}>
        <ScheduledCard pages={scheduled.pages} days={SCHEDULE_DAYS} loading={scheduled.loading} failed={scheduled.failed} />
        <AttentionCard domains={domains} limit={8} onSelect={selectDomain} />
      </div>

      <KpiStrip kpis={kpis} updatedAt={updatedAt} earnings={earningsInfo} />

      <div className="toolbar">
        <input
          type="text"
          className="input grow"
          placeholder="Search sites…"
          aria-label="Search sites"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{ maxWidth: '320px', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
        />
        <label className="small" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
          <span className="muted">Earnings period</span>
          <select
            className="input select"
            aria-label="Earnings period"
            value={period ? period.id : 'last30'}
            onChange={(e) => selectPeriod(e.target.value)}
            style={{ padding: '6px 8px', maxWidth: '280px' }}
          >
            {periods.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
          </select>
        </label>
        <div className="chip-group">
          <button
            type="button"
            className={`chip${attentionOnly ? ' active' : ''}`}
            onClick={() => setAttentionOnly((v) => !v)}
            aria-pressed={attentionOnly}
            title="Show only sites with drafts, pages missing a description, or image errors"
          >
            ⚠ Needs attention{attentionCount > 0 ? <span className="count"> {attentionCount}</span> : null}
          </button>
        </div>
        <label className="small" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
               title={viewOf(columnsView).hint}>
          <span className="muted">Columns</span>
          <select
            className="input select"
            aria-label="Columns"
            value={columnsView}
            onChange={(e) => selectColumns(e.target.value)}
            style={{ padding: '6px 8px' }}
          >
            {COLUMN_VIEWS.map((v) => <option key={v.key} value={v.key}>{v.label}</option>)}
          </select>
        </label>
      </div>

      {filteredDomains.length > 0 ? (
        <SitesTable
          domains={filteredDomains}
          metrics={metrics}
          earnings={earningsInfo}
          trendDays={trendDays}
          view={columnsView}
          isMobile={isMobile}
          onFavoriteToggle={handleFavoriteToggle}
          onSelect={selectDomain}
        />
      ) : domains.length === 0 ? (
        <EmptyState
          title="No sites yet"
          message="Sites are created in the backend; once one exists it will appear here."
        />
      ) : (
        <EmptyState
          title={attentionOnly ? 'Nothing needs attention' : 'No sites match your search'}
          action={(
            <button type="button" className="btn btn-secondary btn-sm"
                    onClick={() => { setSearchQuery(''); setAttentionOnly(false); }}>
              Clear filters
            </button>
          )}
        />
      )}

      <AmazonPanel
        amazon={amazon}
        amazonPeriod={amazonPeriod}
        domains={domains}
        onImport={() => setShowImport(true)}
        onChanged={fetchers.amazon}
        onSelectPeriod={selectPeriod}
      />

      {importModal}
    </div>
  );
};

export default Domains;
